import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import Helmet from "@/components/base/Helmet";
import { videos, categories } from "@/mocks/data";
import type { VideoItem } from "@/mocks/data";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);
  const [filteredVideos, setFilteredVideos] = useState<VideoItem[]>([]);
  const [sortBy, setSortBy] = useState<"hot" | "new" | "rating">("hot");

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    let result = videos.filter((v) => v.category === activeSlug);
    if (sortBy === "rating") {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === "new") {
      result = [...result].sort((a, b) => parseInt(b.year) - parseInt(a.year));
    }
    setFilteredVideos(result);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSlug, sortBy]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const seoData = {
    title: `${currentCategory?.name || "分类"} - 豆花视频 | 91视频97视频51视频免费在线观看`,
    description: `豆花视频${currentCategory?.name || "视频"}分类，海量${currentCategory?.name || "高清"}内容免费在线观看，高清不缓冲，每日更新最新内容。91视频、97视频、51视频一站直达。`,
    keywords: `${currentCategory?.name},豆花视频,91视频,97视频,51视频,免费在线观看,高清视频`,
    canonical: `https://douhuavideo.com/category/${activeSlug}`,
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: seoData.title,
    description: seoData.description,
    url: seoData.canonical,
    isPartOf: {
      "@type": "WebSite",
      name: "豆花视频",
      url: "https://douhuavideo.com",
    },
  };

  return (
    <>
      <Helmet
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogTitle={seoData.title}
        ogDescription={seoData.description}
        ogType="website"
        scriptLd={structuredData}
      />

      <div className="min-h-screen bg-gray-950">
        <Navbar />

        <main className="pt-20 pb-8">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: currentCategory?.name || "分类" },
                ]}
              />

              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-xl md:text-3xl font-bold text-white flex items-center gap-2">
                    <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-xl bg-amber-500/10 text-amber-400">
                      <i className={`${currentCategory?.icon || "ri-film-line"} text-lg md:text-xl`}></i>
                    </div>
                    {currentCategory?.name}
                  </h1>
                  <p className="text-xs md:text-sm text-gray-400 mt-1.5">
                    共 {filteredVideos.length} 部{currentCategory?.name}作品
                  </p>
                </div>

                <div className="flex items-center gap-1.5 bg-gray-900/80 rounded-full p-1 border border-gray-800/50">
                  {(["hot", "new", "rating"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setSortBy(s)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                        sortBy === s
                          ? "bg-amber-500 text-black"
                          : "text-gray-400 hover:text-white"
                      }`}
                    >
                      {s === "hot" && "最热"}
                      {s === "new" && "最新"}
                      {s === "rating" && "高分"}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                        : "text-gray-400 hover:text-white bg-gray-900/60 border border-gray-800/30"
                    }`}
                  >
                    <i className={`${cat.icon} mr-1`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredVideos.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                  {filteredVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-gray-800 text-gray-500 mb-4">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-gray-400">暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}